import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'customer-filter-panel' }

import { ref, computed, watch, onMounted, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import ToggleField from '@/components/PaymentPages/ToggleField.vue'

// Emits a filter_json object matching the backend audience schema:
// { first_name: {contains}, last_name, email, games: {ids}, groups: {ids},
//   tags: {mode, names}, last_visited: {op, days}, lifetime_spend: {op, amount},
//   birthday_month: {mode}, birthday_in_days: {days} }
// Empty object = the whole mailable customer base.
// mode 'ongoing' hides "Birthday this month": an ongoing campaign matches a
// customer on one day, not for a month. The daily condition itself
// (birthday_in_days) is owned by the campaign audience step, which merges it
// onto the filter this panel emits.

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  value: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  mode: { type: String as PropType<'one_time' | 'ongoing'>, default: 'one_time' },
  games: { type: Array as PropType<any[]>, default: () => [] },
  // Group ids the server clamped a restricted caller to; renders the hint
  scopedGroupIds: { type: Array as PropType<number[] | null>, default: null }
},
  emits: ["input"],
  setup(__props, { emit }: { emit: ({ (e: 'input', value: Record<string, any>): void }), expose: any, slots: any, attrs: any }) {

const props = __props





const initial = props.value || {}
const firstName = ref<string>(initial.first_name?.contains || '')
const lastName = ref<string>(initial.last_name?.contains || '')
const email = ref<string>(initial.email?.contains || '')
const gameIds = ref<number[]>(initial.games?.ids ? [...initial.games.ids] : [])
const groupIds = ref<number[]>(initial.groups?.ids ? [...initial.groups.ids] : [])
const tagMode = ref<string>(initial.tags?.mode || 'any')
const tagNames = ref<string[]>(initial.tags?.names ? [...initial.tags.names] : [])
const visitedOp = ref<string>(initial.last_visited?.op || '')
const visitedDays = ref<number | undefined>(initial.last_visited?.days)
const spendOp = ref<string>(initial.lifetime_spend?.op || '')
const spendAmount = ref<number | null>(initial.lifetime_spend?.amount ?? null)
const birthdayThisMonth = ref<boolean>(initial.birthday_month?.mode === 'current')

function buildFilter(): Record<string, any> {
  const filter: Record<string, any> = {}
  if (firstName.value.trim()) filter.first_name = { contains: firstName.value.trim() }
  if (lastName.value.trim()) filter.last_name = { contains: lastName.value.trim() }
  if (email.value.trim()) filter.email = { contains: email.value.trim() }
  if (gameIds.value.length) filter.games = { ids: [...gameIds.value] }
  if (groupIds.value.length) filter.groups = { ids: [...groupIds.value] }
  if (tagNames.value.length) filter.tags = { mode: tagMode.value, names: [...tagNames.value] }
  if (visitedOp.value === 'never') {
    filter.last_visited = { op: 'never' }
  } else if (visitedOp.value && visitedDays.value && visitedDays.value > 0) {
    filter.last_visited = { op: visitedOp.value, days: Math.round(visitedDays.value) }
  }
  if (spendOp.value && spendAmount.value && spendAmount.value > 0) {
    // Threshold input, raw number by design; no cents conversion happens here
    filter.lifetime_spend = { op: spendOp.value, amount: spendAmount.value }
  }
  if (props.mode !== 'ongoing' && birthdayThisMonth.value) filter.birthday_month = { mode: 'current' }
  return filter
}

const hasActiveFilters = computed(() => Object.keys(buildFilter()).length > 0)

// Tag options come from the group's tag library; allow-create keeps legacy
// names (present in saved filters but absent from the library) typeable.
// Selected names not in the library still render as options so they show
// as active chips.
const libraryTagNames = ref<string[]>([])
const tagOptionNames = computed(() => {
  const names = new Set<string>(libraryTagNames.value)
  tagNames.value.forEach((n) => names.add(n))
  return [...names]
})

async function loadLibraryTags() {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const token = user.signInUserSession.idToken.jwtToken
    const companyId = localStorage.getItem('companyId')
    const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
    const response = await axios.get(
      `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/tags`,
      { headers: { Authorization: token } }
    )
    libraryTagNames.value = (response.data || []).map((t: any) => t.name)
  } catch (e) {
    // Library unavailable: the select still works via allow-create
  }
}

// Group options come from the caller's own groups (the endpoint only returns
// groups the caller is linked to), so the picker can never offer a group the
// caller cannot view. Hidden for single-group callers.
const groupOptions = ref<{ id: number; name: string }[]>([])
async function loadGroupOptions() {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const token = user.signInUserSession.idToken.jwtToken
    const companyId = localStorage.getItem('companyId')
    const response = await axios.get(
      `${API_LOCATION}companies/${companyId}/groups`,
      { headers: { Authorization: token } }
    )
    groupOptions.value = (response.data || []).map((g: any) => ({ id: Number(g.id), name: g.name }))
  } catch (e) {
    // Options unavailable: the field stays hidden
  }
}
const scopedGroupNames = computed(() => {
  const ids = new Set((props.scopedGroupIds || []).map(Number))
  return groupOptions.value.filter((g) => ids.has(g.id)).map((g) => g.name)
})

// A server clamp can add groups to the filter after the first preview; keep
// the chips in step without remounting the panel (which would drop typed text).
watch(() => props.value?.groups?.ids, (ids) => {
  const next = Array.isArray(ids) ? ids.map(Number) : []
  const same = next.length === groupIds.value.length && next.every((id, i) => id === groupIds.value[i])
  if (!same) groupIds.value = next
})

onMounted(() => {
  loadLibraryTags()
  loadGroupOptions()
})

function emitFilter() {
  emit('input', buildFilter())
}

function onBirthdayThisMonth(value: boolean) {
  birthdayThisMonth.value = value
  emitFilter()
}

// Switching the campaign type drops the keys the other type does not use.
watch(() => props.mode, () => {
  emitFilter()
})

function onSpendAmount(value: number | null) {
  spendAmount.value = value
  emitFilter()
}

function clearAll() {
  firstName.value = ''
  lastName.value = ''
  email.value = ''
  gameIds.value = []
  groupIds.value = []
  tagNames.value = []
  visitedOp.value = ''
  visitedDays.value = undefined
  spendOp.value = ''
  spendAmount.value = null
  birthdayThisMonth.value = false
  emitFilter()
}

return { __sfc: true,props, emit, initial, firstName, lastName, email, gameIds, groupIds, tagMode, tagNames, visitedOp, visitedDays, spendOp, spendAmount, birthdayThisMonth, buildFilter, hasActiveFilters, libraryTagNames, tagOptionNames, loadLibraryTags, groupOptions, loadGroupOptions, scopedGroupNames, emitFilter, onBirthdayThisMonth, onSpendAmount, clearAll, ToggleField }
}

})